import { useMemo } from "react";
import { kFormatter } from "hooks/utils/formattingUtils";
import "./selected-features-panel.css";
import "./stats-panel-container.css";

// Charts
import LineChart2 from "./charts/LineChart2";
import PieChart2 from "./charts/PieChart2";
import ColoredSVGChart from "./charts/ColoredSVGChart";

import layerGroups from "layers/layers";
import { LayerGroupName } from "types/dataModel";
import { MapboxGeoJSONFeature } from "mapbox-gl";

function MetricContent({
  children,
  contentModifier,
}: {
  children: React.ReactNode;
  contentModifier?: string;
}) {
  return (
    <div
      className={
        "aeb-content-container " + (contentModifier ? contentModifier : "")
      }
    >
      {children}
    </div>
  );
}

function MetricTitle({
  title,
  Icon,
  selected = false,
  clickable = false,
}: {
  title: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  selected?: boolean;
  clickable?: boolean;
}) {
  return (
    <div
      className={
        "aeb-container-title" +
        (selected ? " selected" : "") +
        (clickable ? " clickable" : "")
      }
    >
      <div className="layer-image-container">
        <Icon />
      </div>
      <div className="aeb-title-text-container">
        <h4 className="text-white">{title}</h4>
      </div>
    </div>
  );
}

function TemplateMetricContainer({
  title,
  icon,
  children,
  contentModifier,
  description = "Metric specific description to come here. Only shown when section is hovered.",
}: {
  title: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
  contentModifier?: string;
  description?: string;
}) {
  return (
    <div className="aeb-container text-left">
      <MetricTitle Icon={icon} title={title} />
      <MetricContent contentModifier={contentModifier}>
        <p className="aeb-container-metric-description px-2">{description}</p>
        {children}
      </MetricContent>
    </div>
  );
}

const sum = (acc: number, cur: number) => acc + cur;

const reducers = {
  sum: (values: number[]) => values.reduce(sum, 0),
};

const getStat = (
  metric: string,
  selectedFeatures: MapboxGeoJSONFeature[],
  distinct = false,
) => {
  const values = selectedFeatures
    .map((x) => x.properties?.[metric])
    .filter((value) => value !== undefined && value !== null && !isNaN(value));
  const uniqueValues = distinct ? [...new Set(values)] : values;
  return reducers.sum(uniqueValues);
};

function SelectedFeaturesMetricsPanel({
  selectedFeatures,
  selectedYear,
}: {
  selectedFeatures: MapboxGeoJSONFeature[];
  selectedYear: number;
}) {
  const stockNoMangroves = useMemo(
    () =>
      getStat(`Ben_Stock_${selectedYear}`, selectedFeatures) +
      getStat(`Risk_Stock_${selectedYear}`, selectedFeatures),
    [selectedFeatures, selectedYear],
  );
  const stockWithMangroves = useMemo(
    () => getStat(`Risk_Stock_${selectedYear}`, selectedFeatures),
    [selectedFeatures, selectedYear],
  );
  const popNoMangroves = useMemo(
    () =>
      getStat(`Ben_Pop_${selectedYear}`, selectedFeatures) +
      getStat(`Risk_Pop_${selectedYear}`, selectedFeatures),
    [selectedFeatures, selectedYear],
  );
  const popWithMangroves = useMemo(
    () => getStat(`Risk_Pop_${selectedYear}`, selectedFeatures),
    [selectedFeatures, selectedYear],
  );
  const mangroves1996 = useMemo(
    () => getStat(`Mang_Ha_1996`, selectedFeatures),
    [selectedFeatures],
  );
  const mangroves2010 = useMemo(
    () => getStat(`Mang_Ha_2010`, selectedFeatures),
    [selectedFeatures],
  );
  const mangroves2015 = useMemo(
    () => getStat(`Mang_Ha_2015`, selectedFeatures),
    [selectedFeatures],
  );
  const mangroves2020 = useMemo(
    () => getStat(`Mang_Ha_2020`, selectedFeatures),
    [selectedFeatures],
  );
  const stock1996 = useMemo(
    () => getStat(`Ben_Stock_1996`, selectedFeatures),
    [selectedFeatures],
  );
  const stock2010 = useMemo(
    () => getStat(`Ben_Stock_2010`, selectedFeatures),
    [selectedFeatures],
  );
  const stock2015 = useMemo(
    () => getStat(`Ben_Stock_2015`, selectedFeatures),
    [selectedFeatures],
  );
  const stock2020 = useMemo(
    () => getStat(`Ben_Stock_2020`, selectedFeatures),
    [selectedFeatures],
  );

  const stock_risk_reduct_ratio = useMemo(() => {
    if (stockNoMangroves === 0) return 0;
    return (stockNoMangroves - stockWithMangroves) / stockNoMangroves;
  }, [stockNoMangroves, stockWithMangroves]);

  const piechart_pop_data = [
    { name: "Residual", value: popWithMangroves },
    { name: "Protected", value: popNoMangroves - popWithMangroves },
  ];

  const linechart_data = [
    { name: 1996, benefit: stock1996, mangroves: mangroves1996 },
    { name: 2010, benefit: stock2010, mangroves: mangroves2010 },
    { name: 2015, benefit: stock2015, mangroves: mangroves2015 },
    { name: 2020, benefit: stock2020, mangroves: mangroves2020 },
  ];

  const domain_gap = 0.01;

  const linechart_keys = [
    {
      id: "benefit",
      label: "Benefit",
      fill: "var(--shoreline)",
      axisOrientation: "left",
      tickFormatter: (tick: number) => {
        return "$" + kFormatter(tick);
      },
      domain: [
        Math.min(...linechart_data.map((d) => d.benefit)) * (1 - domain_gap),
        Math.max(...linechart_data.map((d) => d.benefit)) * (1 + domain_gap),
      ],
    },
    {
      id: "mangroves",
      label: "Mangroves",
      fill: "var(--red-tide)",
      axisOrientation: "right",
      tickFormatter: (tick: number) => {
        return kFormatter(tick) + " ha";
      },
      domain: [
        Math.min(...linechart_data.map((d) => d.mangroves)) * (1 - domain_gap),
        Math.max(...linechart_data.map((d) => d.mangroves)) * (1 + domain_gap),
      ],
    },
  ];

  const mangrove_disclaimer_text =
    "This analysis accounts changes in mangroves, population and coastal wealth over time. Flood risk benefits from mangroves grow signficantly with increases in coasal population and wealth.";

  return (
    <>
      <TemplateMetricContainer
        icon={layerGroups[LayerGroupName.BenefitAEB].IconComponent}
        description={layerGroups[LayerGroupName.BenefitAEB].shortDescription}
        title="Economic Benefit"
      >
        <>
          <div className="flex align-center flex-wrap w-full">
            <p className="w-3/5 italic text-right">Damages w/o Mangroves:</p>
            <p className="ml-2 lining-nums">${kFormatter(stockNoMangroves)}</p>
            <p className="w-3/5 italic text-right">- Damages w/ Mangroves:</p>
            <p className="ml-2 lining-nums">
              ${kFormatter(stockWithMangroves)}
            </p>
            <div className="w-3/5 my-1 -translate-x-2 mx-auto pr-4 self-center	 border-solid border-t-[1px] border-trench" />
            <p className="w-3/5 italic text-right">Total Damage Reduction:</p>
            <p className="ml-2 lining-nums">
              <b>${kFormatter(stockWithMangroves - stockNoMangroves)}</b>
            </p>
          </div>
          <div className="flex row w-full pt-8 justify-start">
            <ColoredSVGChart
              risk_reduction_ratio={stock_risk_reduct_ratio}
              no_mang={stockNoMangroves}
              with_mang={stockWithMangroves}
            />
          </div>
        </>
      </TemplateMetricContainer>

      <TemplateMetricContainer
        icon={layerGroups[LayerGroupName.Population].IconComponent}
        description="Changes in mangrove extent and their economic benefits from 1996-2020."
        title="Changing Mangroves & Benefits"
      >
        <>
          <div className="flex align-right flex-wrap">
            <p className="italic text-center px-2">
              {mangrove_disclaimer_text}
            </p>
          </div>
        </>
        <LineChart2 data={linechart_data} keys={linechart_keys} />
      </TemplateMetricContainer>

      <TemplateMetricContainer
        icon={layerGroups[LayerGroupName.Population].IconComponent}
        description={layerGroups[LayerGroupName.Population].shortDescription}
        title="Population Benefit"
      >
        <>
          <div className="flex align-center flex-wrap">
            <p className="w-4/5 italic text-center">
              People flooded w/o Mangroves:
            </p>
            <p className="ml-2 lining-nums">{kFormatter(popNoMangroves)}</p>
            <p className="w-4/5 italic text-center">
              - People flooded w/ Mangroves:
            </p>
            <p className="ml-2 lining-nums">{kFormatter(popWithMangroves)}</p>
            <div className="w-4/5 my-1 -translate-x-2 mx-auto pr-4 self-center	 border-solid border-t-[1px] border-trench" />
            <p className="w-3/5 italic text-right">Total Protection:</p>
            <p className="ml-2 lining-nums">
              <b>{kFormatter(popWithMangroves - popNoMangroves)}</b>
            </p>
          </div>
        </>
        <PieChart2 data={piechart_pop_data} type="STOCK" />
      </TemplateMetricContainer>
    </>
  );
}

export default SelectedFeaturesMetricsPanel;
